import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, filter, map } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { CoreModuleName } from '@app/core/models/core-constant.model';
import { EidosConfigService } from '@common/config/eidos-config.service';
import { IEidosModuleConfiguration } from '@common/config/environment.interface';
import { DynamicApiResponse, DynamicApiResponsePaginatedModel } from '@common/models/dynamic-api-response.model';
import { CoreFormatService } from '@common/services/core-format.service';
import { EidosBaseApiService } from '@common/services/eidos-base-api.service';
import { EidosLogService } from '@common/services/eidos-log.service';
import { EidosSecurityService } from '@common/services/eidos-security.service';

import { IReservationModuleAPIConfiguration } from '@app/res-common/models/rescom.model';
import { IResBookingManageSuiteRemarkParams } from '../models/res-booking.model';
import { IReservationSuiteAmenity } from '../models/res-cached-data.models';
import { IResGtyUpdateParams, IResInventoryUpdateParams, IReservationApiCruiseInventoryParams, IReservationApiManageConnectedSuite, ResInventoryVoyageGTY, ReservationCruiseAvailabilityModel, ReservationCruiseInventoryModel, ReservationCruiseSuite, ReservationCruiseSuiteListAvailabilityModel, ReservationCruiseSuiteListConnectedSuite, ReservationGroupVoyageList, ResInventoryAvailabilityByCategory, ResInventoryByCategory, ResInventoryByCategoryModel, ResPriceSuiteByVoyage, ResInventoryVoyagePackage } from '../models/res-cruise-availability.models';
import { ReservationCruise, ReservationCruiseItinerary, ReservationCruiseSuiteProfitability } from '../models/res-cruise.models';
import { ResOwner } from '../models/res-owner.model';
import { IReservationApiPackageEcmData } from "../models/res-package-detail.model";
import { IResApiPackageOptionToSuiteCategory, ResCruisePriceType, ResInventoryPackage, ResInventoryPriceType, ResPackageOptionToSuiteCategory } from '../models/res-package.model';
import { ResPromoShip } from '../models/res-promo.model';
import { IResSuiteDeckplanParams, IResVoyageSearchRequiredParams, IReservationApiCruiseSuiteDeckplanModel, ResBargeVoyageParams, ReservationCruiseSuiteDeckplanModel } from '../models/res-search.models';

import { IResVillaEditPrice } from './reservation-api.interfaces';
@Injectable({
  providedIn: 'root'
})
export class ReservationInventoryApiService extends EidosBaseApiService {

  constructor(
    public configService: EidosConfigService,
    public eidosSecurityService: EidosSecurityService,
    public http: HttpClient,
    public eidosLogService: EidosLogService,
    public domSanitizer: DomSanitizer,
    public coreFormatService: CoreFormatService
  ) {
    super(coreFormatService, configService, eidosLogService, eidosSecurityService, http);
    this.configService.currentModulesConfig
      .pipe(
        filter(
          (modules: Array<IEidosModuleConfiguration>) => !!modules.find((module) => module.moduleName === CoreModuleName.Reservation)
        )
      )
      .subscribe((modules) => {
        const module = modules.find((module) => module.moduleName === CoreModuleName.Reservation);
        if (module) {
          const reservationModuleAPI = module.API as IReservationModuleAPIConfiguration;
          this.urlAPI = reservationModuleAPI.url;
          this.staticUrlAPI = reservationModuleAPI.staticUrl;
          this.pluginUrlAPI = reservationModuleAPI.pluginUrl;
          this.apiVersion = reservationModuleAPI.version;
          if (reservationModuleAPI.headers) {
            this.headers = {
              ...reservationModuleAPI.headers,
              ...this.headers
            };
          }
        }
      });
  }

  editVillaPrice(params: IResVillaEditPrice) {
    return this.callDynamicAPI(
      'bkgitem/editVillaPrice',
      params,
      'POST'
    ).pipe(
      map<DynamicApiResponse, any>(response => response.body)
    );
  }

  getCharterBookingParams(voyageID: number): Observable<ResBargeVoyageParams> {
    return this.callDynamicAPI('bkg/getBkgCharterParams', { VoyageID: voyageID }).pipe(
      map<DynamicApiResponse, ResBargeVoyageParams>((response) => {
        return new ResBargeVoyageParams(response.body[0])
      })
    )
  }

  /**
   * Retrieves cruise availability
   *
   * @return {*}
   * @memberof ReservationInventoryApiService
   */
  getCruiseAvailabilityNew(owner: ResOwner, payload: IReservationApiCruiseInventoryParams, showAllSuites: boolean = false) {
    if (owner.isValid) {
      if (owner.isAgent()) {
        payload.OwnerType = 'AGENCY'
        payload.AgencyCod = owner.agencyID;
      } else if (owner.isDirect()) {
        payload.OwnerType = "DIRECT";
      }
    }
    const url = showAllSuites ? 'inventory/selectAll' : 'inventory/selectCapacity';
    return this.callDynamicAPI(url, payload, showAllSuites ? 'GET' : 'POST').pipe(
      map<DynamicApiResponse, Array<ReservationCruiseAvailabilityModel>>((response) => {
        const data = showAllSuites ? response.body : response.body.Voyage;
        return data.map((aval: any) => new ReservationCruiseAvailabilityModel(aval))
      }
      )
    );
  }

  /**
   * Get inventory setup "Inventory By Category" tab data
   *
   * @return {*}
   * @memberof ReservationInventoryApiService
   */
  getInventoryByCategory(voyageID: number) {
    return this.callDynamicAPI('inventory/selectInventoryByCategory', { VoyageID: voyageID }, 'GET').pipe(
      map<DynamicApiResponse, ResInventoryByCategory[]>((response) =>
        response.body.map((item: any) => new ResInventoryByCategory(item))
      )
    );
  }

  getInventoryConnectedSuites(voyageID: number): Observable<ReservationCruiseSuiteListConnectedSuite[]> {
    return this.callDynamicAPI('inventorySetup/getConnectedSuites', { VoyageID: voyageID }, 'GET').pipe(
      map<DynamicApiResponse, ReservationCruiseSuiteListConnectedSuite[]>((response) => {
        const body = response.body as any;
        const rows = Array.isArray(body)
          ? body
          : body?.DT0 ?? body?.ConnectedSuites ?? body?.Items ?? [];

        return rows.map((item: any) => new ReservationCruiseSuiteListConnectedSuite(item));
      })
    );
  }

  getInventoryPackage(voyageID: number): Observable<ResInventoryPackage[]> {
    return this.callDynamicAPI('inventory/getPackages', { VoyageID: voyageID }).pipe(
      map<DynamicApiResponse, ResInventoryPackage[]>((response) => {
        return response.body.map((data: any) => new ResInventoryPackage(data))
      })
    )
  }

  getInventoryPriceTypes(voyageID: number): Observable<ResInventoryPriceType[]> {
    return this.callDynamicAPI('inventory/getPriceTypes', { VoyageID: voyageID }).pipe(
      map<DynamicApiResponse, ResInventoryPriceType[]>((response) => {
        return response.body.map((priceType: any) => new ResInventoryPriceType(priceType));
      })
    );
  }

  /**
   * Get inventory setup "Availability" tab data
   *
   * @return {*}
   * @memberof ReservationInventoryApiService
   */
  getInventorySetupAvailability(voyageID: number, currency: string, priceTypeID: number) {
    return this.callDynamicAPI('inventory/selectAvailabilityByCategory', { ID: voyageID, Currency: currency, PriceTypeID: priceTypeID }, 'GET').pipe(
      map<DynamicApiResponse, ResInventoryAvailabilityByCategory[]>((response) =>
        (response.body.DT0 ?? []).map((item: any) => new ResInventoryAvailabilityByCategory(item))
      )
    );
  }

  getInventorySuiteList(payload: IResVoyageSearchRequiredParams, amenities: IReservationSuiteAmenity[] = []) {
    return this.callDynamicAPI('inventory/suiteList', payload, 'GET').pipe(
      map<DynamicApiResponse, { suites: ReservationCruiseSuiteListAvailabilityModel[], connectedSuites: ReservationCruiseSuiteListConnectedSuite[] }>((response): { suites: ReservationCruiseSuiteListAvailabilityModel[], connectedSuites: ReservationCruiseSuiteListConnectedSuite[] } =>
      ({
        suites: response.body.DT0.map(
          (reservationCruiseAvail: any) =>
            new ReservationCruiseSuiteListAvailabilityModel(reservationCruiseAvail, amenities)
        ),
        connectedSuites: response.body.DT1.map(
          (connectedSuite: any) =>
            new ReservationCruiseSuiteListConnectedSuite(connectedSuite)
        )
      })
      )
    );
  }

  getInvetoryVoyagePackages(voyageID: number): Observable<ResInventoryVoyagePackage[]> {
    return this.callDynamicAPI('inventory/getInventoryVoyagePackages', { VoyageID: voyageID }).pipe(
      map<DynamicApiResponse, ResInventoryVoyagePackage[]>((response) => response.body.map((data: any) => new ResInventoryVoyagePackage(data)))
    );
  }

  public getPackageOptionToSuiteCategoryList(packageID?: number | string): Observable<ResPackageOptionToSuiteCategory[]> {
    return this.callDynamicAPI('package/getOptionSuiteCategory', { PackageID: packageID }).pipe(
      map<DynamicApiResponse, ResPackageOptionToSuiteCategory[]>((response) => {
        return response.body.map((c: any) => new ResPackageOptionToSuiteCategory(c));
      })
    )
  }

  public getPackageVoyagePrices(voyageID?: number): Observable<ResCruisePriceType[]> {
    return this.callDynamicAPI('package/selectPackageVoyagePrices', { VoyageID: voyageID }).pipe(
      map<DynamicApiResponse, ResCruisePriceType[]>((response) => {
        return response.body.map((c: any) => new ResCruisePriceType(c));
      })
    )
  }

  /**
   * Get inventory setup "Price Suite By Voyage" tab data
   *
   * @return {*}
   * @memberof ReservationInventoryApiService
   */
  getPriceSuiteByVoyage(voyageID: number) {
    return this.callDynamicAPI('inventory/selectPriceSuiteByVoyage', { VoyageID: voyageID }, 'GET').pipe(
      map<DynamicApiResponse, ResPriceSuiteByVoyage[]>((response) =>
        response.body.map((item: any) => new ResPriceSuiteByVoyage(item))
      )
    );
  }

  getPromotionShips(promoID?: number) {
    return this.callDynamicAPI('promoVoyages/getShips', { PromoID: promoID }).pipe(
      map<DynamicApiResponse, Array<ResPromoShip>>((response) =>
        response.body.map((p: any) => new ResPromoShip(p))
      )
    );
  }

  /**
   * Get suites deckplan of a cruise
   *
   * @param {IResSuiteDeckplanParams} payload
   * @return {*}
   * @memberof ReservationInventoryApiService
   */
  getSuiteDeckplan(payload: IResSuiteDeckplanParams) {
    //TODO cache data
    return this.callDynamicAPI('suite/deckplan', payload, 'GET').pipe(
      map<DynamicApiResponse, ReservationCruiseSuiteDeckplanModel>(
        (response) => new ReservationCruiseSuiteDeckplanModel(response.body as IReservationApiCruiseSuiteDeckplanModel)
      )
    );
  }

  public getSuiteStatus(params: { VoyageID?: number, PriceTypeID?: number }): Observable<Array<any>> {
    return this.callDynamicAPI('inventory/selectStatus', params).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body;
      })
    )
  }

  /**
   * Gets ECM data details for a voyage
   *
   * @param {number} voyageID
   * @return {*}  {Observable<Array<IReservationApiPackageEcmData>>}
   * @memberof ReservationInventoryApiService
   */
  getVoyageEcmData(voyageID: number): Observable<Array<IReservationApiPackageEcmData>> {
    return this.getDynamicAPI('voyageecmdata/get', { VoyageID: voyageID }).pipe(
      map<DynamicApiResponse, Array<IReservationApiPackageEcmData>>((response) => {
        return response.body as Array<IReservationApiPackageEcmData>;
      }),
      catchError<any, Array<IReservationApiPackageEcmData>>((_: any) => new Array<IReservationApiPackageEcmData>())
    );
  }

  getVoyageGroupList(voyageID?: number): Observable<any[]> {
    return this.callDynamicAPI('inventory/getSetupGroups', { VoyageID: voyageID }).pipe(
      map<DynamicApiResponse, any[]>((response) =>
        response.body.map((g: any) => new ReservationGroupVoyageList(g))
      )
    );
  }

  getVoyageInventoryBySuite(voyageID: number) {
    return this.callDynamicAPI('inventory/selectBySuite', { VoyageID: voyageID }, 'GET').pipe(
      map<DynamicApiResponse, ResInventoryVoyageGTY[]>((response) =>
        response.body.map((item: any) => new ResInventoryVoyageGTY(item))
      )
    );
  }

  getVoyageInventoryBySuiteBookings(voyageID: number) {
    return this.callDynamicAPI('inventory/selecyBySuiteBookings', { VoyageID: voyageID }, 'GET').pipe(
      map<DynamicApiResponse, ResInventoryVoyageGTY[]>((response) =>
        response.body.map((item: any) => new ResInventoryVoyageGTY(item))
      )
    );
  }

  getVoyageInventoryForInteractiveDeckplan(voyageID: number): Observable<ResInventoryByCategoryModel[]> {
    return this.callDynamicAPI('inventory/selectInventoryByCategory', { VoyageID: voyageID }, 'GET').pipe(
      map<DynamicApiResponse, ResInventoryByCategoryModel[]>((response) =>
        (response.body ?? []).map((item: any) => new ResInventoryByCategoryModel(item))
      )
    );
  }

  getVoyageInventoryGTY(voyageID: number) {
    return this.callDynamicAPI('inventory/selectGTY', { VoyageID: voyageID }, 'GET').pipe(
      map<DynamicApiResponse, ResInventoryVoyageGTY[]>((response) =>
        response.body.map((item: any) => new ResInventoryVoyageGTY(item))
      )
    );
  }

  public getVoyageInventoryInfo(voyageID: number, endpoint: string = 'booking/getVoyageInventoryInfo') {
    return this.callDynamicAPI(endpoint, { VoyageID: voyageID }, 'POST').pipe(
      map<DynamicApiResponse, DynamicApiResponsePaginatedModel<ReservationCruise>>((response) => {
        const paginatedModel = new DynamicApiResponsePaginatedModel<ReservationCruise>(response);
        paginatedModel.content = response.body.map(
          (cruise: any) => new ReservationCruise(cruise)
        );
        return paginatedModel;
      })
    );
  }

  /**
   * Get itinerary of a voyage
   *
   * @param {number} voyageID
   * @return {*}  {Observable<ReservationCruiseItinerary[]>}
   * @memberof ReservationInventoryApiService
   */
  getVoyageItinerary(voyageID: number): Observable<ReservationCruiseItinerary[]> {
    return this.getDynamicAPI('cruise/itinerary', { VoyageID: voyageID }).pipe(
      map<DynamicApiResponse, ReservationCruiseItinerary[]>((response) => response.body.map((i: any) => new ReservationCruiseItinerary(i))),
      catchError<any, ReservationCruiseItinerary[]>((_: any) => [])
    );
  }

  /**
   * Get booked suite of a voyages and their bkgID
   *
   * @param {number} voyageID
   * @return {*}  {Observable<ReservationApiBookedSuite[]>}
   * @memberof ReservationInventoryApiService
   */
  getVoyageSuiteAndBookingList(voyageID: number, amenities: IReservationSuiteAmenity[] = [], includeWL: boolean = true): Observable<ReservationCruiseSuite[]> {
    return this.callDynamicAPI('inventory/suiteBookedNew', { VoyageID: voyageID, IncludeWL: includeWL ? 'Y' : 'N' }, 'GET').pipe(
      map<DynamicApiResponse, ReservationCruiseSuite[]>(response => {

        const suites: any[] = response.body.Suites ? response.body.Suites : []
        const bookingInfo: any[] = response.body.BookingInfo ? response.body.BookingInfo : []
        const guestBooking: any[] = response.body.GuestBooking ? response.body.GuestBooking : []
        const connectedSuite: any[] = response.body.ConnectedSuite ? response.body.ConnectedSuite : []

        return suites.map(s => {
          s.BookingInfo = bookingInfo.filter(bi => bi.SuiteID == s.SuiteID)
            .map(p => {
              p.GuestBooking = guestBooking.filter(item => item.BkgID == p.BkgID)
              return p
            })

          s.ConnectedSuite = connectedSuite.find(item => item.SuiteNo == s.SuiteNo)

          return new ReservationCruiseSuite(s, amenities);
        })

        //response.body.Suites.map((sl: IReservationApiCruiseSuite) => new ReservationCruiseSuite(sl, amenities))
      })
    )
  }

  /**
   * Get voyage suite profitability
   *
   * @param {number} voyageID
   * @return {*}  {Observable<ReservationCruiseSuiteProfitability[]>}
   * @memberof ReservationInventoryApiService
   */
  getVoyageSuiteProfitability(voyageID: number, priceTypeID: number): Observable<ReservationCruiseSuiteProfitability[]> {
    return this.callDynamicAPI('voyage/suiteProfitability', { VoyageID: voyageID, PriceTypeID: priceTypeID }, 'GET').pipe(
      map<DynamicApiResponse, ReservationCruiseSuiteProfitability[]>((response) =>
        response.body.map((sl: any) => new ReservationCruiseSuiteProfitability(sl))
      )
    );
  }

  gtyUpdate(payload: IResGtyUpdateParams) {
    return this.callDynamicAPI('gty/update', payload, 'POST').pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  /**
   * update inventory suites of a cruise
   *
   * @param {IResInventoryUpdateParams} payload
   * @return {*}
   * @memberof ReservationInventoryApiService
   */
  inventoryUpdate(payload: IResInventoryUpdateParams) {
    return this.callDynamicAPI('inventory/update', payload, 'POST').pipe(
      map<DynamicApiResponse, object>((response) => response.body)
    );
  }

  manageInventoryConnectedSuites(params: { connections: IReservationApiManageConnectedSuite[], voyageID: number }): Observable<any> {
    return this.callDynamicAPI(
      'inventorySetup/manageConnectedSuites',
      params,
      'POST'
    );
  }

  public managePackageOptionToSuiteCategoryList(params: IResApiPackageOptionToSuiteCategory[]) {
    const packageID = params?.[0].PackageID;
    return this.callDynamicAPI('package/manageOptionSuiteCategory', { PackageOption2SuiteCategory: params, PackageID: packageID }, 'POST').pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  manageSuiteRemark(params: IResBookingManageSuiteRemarkParams): Observable<any> {
    return this.postDynamicAPI('booking/manageSuiteRemark', params);
  }

  /**
   * Select inventory capacity of a cruise with multisuite
   *
   * @return {*}
   * @memberof ReservationInventoryApiService
   */
  retriveInventorySelectCapacity(payload: IResVoyageSearchRequiredParams, owner: ResOwner) {
    if (owner.isValid) {
      if (owner.isAgent()) {
        payload.OwnerType = 'AGENCY'
        payload.AgencyCod = owner.agencyID;
      } else if (owner.isDirect()) {
        payload.OwnerType = "DIRECT";
      }
    }
    return this.callDynamicAPI('inventory/selectcapacity', payload, 'POST').pipe(
      map<DynamicApiResponse, ReservationCruiseInventoryModel>((response) => new ReservationCruiseInventoryModel(response.body))
    );
  }

  public saveSuiteStatus(params: { VoyageID: number, PriceTypeID: number, SuiteCategoryCod: string, Status: string }) {
    return this.postDynamicAPI('inventory/manageStatus', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }
}
